import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Render markdown-lite body: paragraphs separated by blank lines,
 * lines beginning with "- " become a shared bullet list.
 * Deliberately tiny — we don't need a full markdown parser here.
 */
function renderBody(body) {
  if (!body) return null;
  const blocks = body.split(/\n\s*\n/); // paragraph splits
  return blocks.map((block, i) => {
    const lines = block.split('\n').map(l => l.trimEnd());
    const bulletLines = lines.filter(l => l.startsWith('- '));
    // A block is a list iff every non-empty line is a bullet.
    const nonEmpty = lines.filter(l => l.trim() !== '');
    if (nonEmpty.length > 0 && nonEmpty.every(l => l.startsWith('- '))) {
      return (
        <ul key={i} className="list-disc pl-5 space-y-1 my-2 text-sm text-stone-700">
          {bulletLines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}
        </ul>
      );
    }
    return (
      <p key={i} className="text-sm text-stone-700 my-2 leading-relaxed">
        {block}
      </p>
    );
  });
}

/**
 * Dialog that shows a single help topic. Fetches the full topic list
 * on open and picks out the one matching ``topicKey``. All logged-in
 * users can read the content; superusers edit it via System Settings.
 */
export function HelpDialog({ open, onOpenChange, topicKey }) {
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !topicKey) return;
    setLoading(true);
    axios.get(`${API}/help-content`, { withCredentials: true })
      .then(r => {
        const found = (r.data || []).find(t => t.key === topicKey);
        setTopic(found || null);
      })
      .catch(() => toast.error('Could not load help content'))
      .finally(() => setLoading(false));
  }, [open, topicKey]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`help-dialog-${topicKey}`}>
        <DialogHeader>
          <DialogTitle style={{ color: '#2D4A3E', fontFamily: 'Manrope, sans-serif' }}>
            {topic?.title || (loading ? 'Loading…' : 'Help')}
          </DialogTitle>
        </DialogHeader>

        {topic ? (
          <div className="space-y-3">
            {topic.intro && (
              <p className="text-base font-medium text-stone-800">{topic.intro}</p>
            )}
            <div>{renderBody(topic.body)}</div>
            {topic.related_links?.length > 0 && (
              <div className="pt-3 border-t border-stone-200">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Jump to
                </div>
                <div className="flex flex-wrap gap-2">
                  {topic.related_links.map((rl, i) => (
                    <Link key={i} to={rl.url} onClick={() => onOpenChange(false)}>
                      <Button variant="outline" size="sm" data-testid={`help-link-${topicKey}-${i}`}>
                        {rl.label}
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          !loading && (
            <p className="text-sm text-muted-foreground">
              No help content configured yet. A super user can add it in System Settings.
            </p>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
